import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "sama-browser-voice-command-settings";

const DEFAULT_SETTINGS = {
  enabled: true,
};

const normalizeSettings = (value) => ({
  enabled: value?.enabled !== false,
});

const readStoredSettings = () => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error("Failed to read browser voice command settings:", error);
    return DEFAULT_SETTINGS;
  }
};

const BrowserVoiceCommandContext = createContext({
  settings: DEFAULT_SETTINGS,
  setEnabled: () => {},
});

export function BrowserVoiceCommandProvider({ children }) {
  const [settings, setSettings] = useState(readStoredSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save browser voice command settings:", error);
    }
  }, [settings]);

  const setEnabled = useCallback((enabled) => {
    setSettings((prev) => ({ ...prev, enabled: Boolean(enabled) }));
  }, []);

  const value = useMemo(() => ({ settings, setEnabled }), [settings, setEnabled]);

  return (
    <BrowserVoiceCommandContext.Provider value={value}>
      {children}
    </BrowserVoiceCommandContext.Provider>
  );
}

export const useBrowserVoiceCommandSettings = () => useContext(BrowserVoiceCommandContext);
